import { useCallback, useMemo, useRef } from 'react'
import { yfit } from './yFitTrace'

/**
 * useYCamera — the Y machine.
 *
 * OWNS (writes): mode (fit | follow | explore), centre, the user span
 * (userHalf), the explore centre (userCentre), the fit hold, the follow
 * centre, the Y-scale gesture window, and the sub-bucket residual. It is the
 * ONLY writer of halfSpan.
 *
 * MODES
 *   fit     — default. No user span. Centre/halfSpan from the visible scan
 *             (expand-only hold while idle, stamped while X moves).
 *   follow  — the user set the span (Y-axis wheel / phone axis drag) and the
 *             live candle is in view. The live footprint is kept inside the
 *             central FOLLOW_BAND_FRAC of the view; when it touches the band
 *             edge the view recentres on it. No damper, no chase.
 *   explore — a body drag with a Y component while the span is the user's.
 *             Follow and auto-fit stop; centre is userCentre, moved only by
 *             panBy(). Ends only on reset() (Y-axis / footer double-click),
 *             token switch or a log/linear flip.
 *
 * CLAMP (user span only, every frame): with the visible candles' raw
 * extremes lo/hi and h = halfSpan,
 *   data taller than view: centre in [lo + h - row, hi - h + row]
 *   data fits:             centre in [hi - h - row, lo + h + row] (on screen)
 * That is also what keeps a fast Y scroll from dropping the view.
 *
 * MUST NOT: choose or mint the lattice step (step is an INPUT), touch X,
 * paint anything. No paint imports in this file, ever.
 *
 * CALL CONTRACT: frame() exactly once per grid pass, before slide().
 */

// Live footprint is held inside this central fraction of the view (0.70 =
// 15% margin top and bottom). Touching the band edge recentres.
export const FOLLOW_BAND_FRAC = 0.70

// Gesture window. Covers the wheel's rAF glide tail.
export const Y_SCALE_GESTURE_MS = 200

// ONE buffer formula for the default fit. Camera, bucket sizer and the X
// glide destinations all call this. Additive padding, a fraction of the DATA
// range; step is only the flat-data fallback.
export function computeRangeBuffer(dataRange, step) {
  return dataRange > 0 ? dataRange * 0.025 : step
}

// RH BY CONSTRUCTION: the price window 2*halfSpan maps onto H exactly.
export function rowHeightForSpan(containerHeight, step, halfSpan) {
  if (!(containerHeight > 0) || !(step > 0) || !(halfSpan > 0)) return 0
  return (containerHeight * step) / Math.max(1e-12, 2 * halfSpan)
}

export default function useYCamera(traceRef = null) {
  // TEMP trace tag ({ id, tf, H } owned by the chart) — see yFitTrace.
  const yfitT = (reason, d = {}) => yfit(reason, { ...(traceRef && traceRef.current), ...d })
  // User-owned span, ABSOLUTE, working space (ln when log). null = default fit.
  // Set only by the Y-axis wheel / phone axis drag; cleared only by reset /
  // clearPin (double-click, token switch) and a log/linear flip.
  const userHalfRef = useRef(null)
  // Explore centre, working space. null = not exploring.
  const userCentreRef = useRef(null)
  // Follow: the held centre. Moves only when the live footprint touches the
  // band edge.
  const followCentreRef = useRef(null)
  // Last frame's output — continuity for arm / follow entry / drag px→price.
  const lastRef = useRef(null)
  // (ytickcap) Last frame's span floor (working space): one tick per row at
  // the target row height. 0 = no tick known.
  const floorRef = useRef(0)
  // Extremes FIT has committed to. Held while the data stays inside them.
  const fitHoldRef = useRef(null)
  // Fit window last painted. A price-bucket commit changes step only: keep
  // centre and halfSpan until the scan actually changes.
  const fitWinRef = useRef(null)
  const stepRef = useRef(null)
  const quietHoldRef = useRef(false)
  const gestureUntilRef = useRef(0)

  // Optional ms keeps the gesture window alive for sparse touch moves
  // (phone Y-axis drag). dragActiveRef covers the whole finger-down.
  const dragActiveRef = useRef(false)
  const markGesture = useCallback((ms) => {
    const dur = (typeof ms === 'number' && ms > 0) ? ms : Y_SCALE_GESTURE_MS
    gestureUntilRef.current = performance.now() + dur
  }, [])
  const setDragActive = useCallback((active) => {
    dragActiveRef.current = !!active
  }, [])
  const clearGesture = useCallback(() => {
    gestureUntilRef.current = 0
  }, [])

  return useMemo(() => ({
    markGesture,
    setDragActive,
    clearGesture,

    // ── Read-only probes ──
    isGesturing: () => (
      performance.now() < gestureUntilRef.current || dragActiveRef.current
    ),
    // "Pinned" = the span is the user's (follow / explore / pinned fit).
    isPinned: () => userHalfRef.current != null,
    isExploring: () => userHalfRef.current != null && userCentreRef.current != null,
    userHalf: () => userHalfRef.current,
    peekHalf: () => (lastRef.current ? lastRef.current.halfSpan : null),
    floorHalf: () => floorRef.current,
    peekFitHold: () => fitHoldRef.current,

    // ── User span ──
    // Arm from what is on screen (painted half + centre) so the first frame
    // after the arm is a no-op. No-op when already armed.
    armSpan(half, centre) {
      if (userHalfRef.current != null) return
      if (!(half > 0)) return
      userHalfRef.current = half
      if (Number.isFinite(centre)) lastRef.current = { centre, halfSpan: half }
      followCentreRef.current = null
    },
    setHalf(half) {
      if (userHalfRef.current == null || !(half > 0)) return
      userHalfRef.current = half
    },

    // ── Explore ──
    // Enter at the painted centre (or the last frame's). Follow stops.
    enterExplore(centre) {
      if (userHalfRef.current == null) return
      if (userCentreRef.current != null) return
      const c = Number.isFinite(centre) ? centre
        : (lastRef.current ? lastRef.current.centre : null)
      if (!Number.isFinite(c)) return
      userCentreRef.current = c
      followCentreRef.current = null
    },
    panBy(d) {
      if (userCentreRef.current == null || !Number.isFinite(d)) return
      userCentreRef.current += d
    },

    // User span + explore only. Token switch.
    clearPin() {
      yfitT('clearPin')
      userHalfRef.current = null
      userCentreRef.current = null
      followCentreRef.current = null
    },

    // Follow centre only — the TF flip (re-enter follow from what is shown).
    clearTrail(reason = '') {
      yfitT(`clearTrail ${reason}`)
      followCentreRef.current = null
    },

    // Fit hold only — TF data arrival / new candle refit. User span stays.
    clearFitHold(reason = '') {
      const ph = fitHoldRef.current
      yfitT(`clearFitHold ${reason}`, { lo: ph?.min, hi: ph?.max, extra: 'held range dropped' })
      fitHoldRef.current = null
      fitWinRef.current = null
      quietHoldRef.current = false
    },

    // Full stand-down: Y-axis / footer double-click, token switch. Back to
    // the default fit.
    reset() {
      yfitT('reset')
      userHalfRef.current = null
      userCentreRef.current = null
      followCentreRef.current = null
      lastRef.current = null
      fitHoldRef.current = null
      fitWinRef.current = null
      stepRef.current = null
      quietHoldRef.current = false
    },

    /**
     * One camera frame.
     *
     * min/max     raw scan extremes of the visible candles, working space
     * step        committed lattice step, working space — READ ONLY
     * highlight   live price, RAW (not ln) — 0 when unknown
     * log         true when the working space is ln(price)
     * xMoving     drag / x-glide / x-zoom target live
     * liveInView  newest candle inside the window
     * logChanged  log space flipped this frame (drops everything)
     * liveLo/Hi   live candle footprint extremes, working space (NaN = none)
     * minHalfLin  PRICE half-span at which one tick fills one target-height
     *             row (H * tick / (2 * targetRH)); 0 = no tick known
     * tfHold      inside a timeframe-switch window (FPCC tfWinRef): the fit
     *             is the ONLY Y writer — expand-only even if X moves, no
     *             rebucket (quiet) hold, no follow-band recentre. A user
     *             span keeps its half; its centre is only clamped.
     */
    frame({ min, max, step, highlight, log, xMoving, liveInView, logChanged, liveLo, liveHi, minHalfLin = 0, tfHold = false, clampMin, clampMax }) {
      // Linear ↔ ln are different spaces; nothing absolute survives a flip.
      if (logChanged) {
        userHalfRef.current = null
        userCentreRef.current = null
        followCentreRef.current = null
        lastRef.current = null
        fitHoldRef.current = null
        fitWinRef.current = null
        stepRef.current = null
        quietHoldRef.current = false
      }
      const stepChanged = stepRef.current != null && stepRef.current !== step
      stepRef.current = step

      const gesturing = performance.now() < gestureUntilRef.current || dragActiveRef.current
      const userSpan = userHalfRef.current != null
      if (!userSpan) userCentreRef.current = null
      const exploring = userSpan && userCentreRef.current != null
      const mode = exploring
        ? 'explore'
        : ((userSpan && liveInView && highlight > 0) ? 'follow' : 'fit')

      // ── FIT HOLD (fit mode only) ──
      let lo = min
      let hi = max
      if (mode !== 'fit') {
        fitHoldRef.current = null
        fitWinRef.current = null
      } else if (xMoving && !tfHold) {
        fitHoldRef.current = { min: lo, max: hi }
      } else {
        const prev = fitHoldRef.current
        if (prev) {
          lo = Math.min(prev.min, lo)
          hi = Math.max(prev.max, hi)
        }
        fitHoldRef.current = { min: lo, max: hi }
      }
      const dataRange = hi - lo
      const buffer = computeRangeBuffer(dataRange, step)
      let halfSpan = userSpan ? userHalfRef.current : (dataRange / 2) + buffer

      // ── CENTRE ──
      let _why = tfHold ? 'tf:' : ''
      let centre
      if (mode === 'explore') {
        centre = userCentreRef.current
        followCentreRef.current = null
      } else if (mode === 'follow') {
        let c = followCentreRef.current
        if (c == null) {
          // Entering follow: keep what is on screen.
          c = lastRef.current ? lastRef.current.centre : (hi + lo) / 2
        }
        const hlV = log ? (highlight > 0 ? Math.log(highlight) : NaN) : highlight
        let fLo = liveLo
        let fHi = liveHi
        if (!(Number.isFinite(fLo) && Number.isFinite(fHi) && fHi >= fLo)) {
          fLo = hlV
          fHi = hlV
        }
        const band = halfSpan * FOLLOW_BAND_FRAC
        // A footprint taller than the band can never sit inside it — track
        // the live price instead so the centre does not re-fire every frame.
        if (fHi - fLo > 2 * band && Number.isFinite(hlV)) {
          fLo = hlV
          fHi = hlV
        }
        if (!tfHold && Number.isFinite(fLo) && Number.isFinite(fHi)
            && (fHi > c + band || fLo < c - band)) {
          c = (fHi + fLo) / 2
          _why += '+recentre'
        }
        centre = c
      } else {
        followCentreRef.current = null
        // ONE FIT: the midpoint of the range on screen, one row low so the
        // lowest cell clears the frame edge. A price-bucket commit keeps the
        // centre and halfSpan already on screen.
        const mid = (hi + lo) / 2
        const prevFit = fitWinRef.current
        const userH = userSpan ? userHalfRef.current : null
        // Inside a TF window a step change is the new interval's rebucket:
        // it re-fits (centre = mid − NEW step) instead of holding a centre
        // that was placed one OLD row low.
        const sameFit = !!(prevFit && !(tfHold && stepChanged)
          && prevFit.lo === lo && prevFit.hi === hi
          && prevFit.userH === userH)
        if (sameFit) {
          centre = prevFit.centre
          if (!userSpan) halfSpan = prevFit.halfSpan
          // Rebucket hold — never inside a TF window: the new interval's
          // rebucket must adopt the new fit, not carry the old lattice half.
          if (stepChanged && !userSpan && !tfHold) quietHoldRef.current = true
          _why += 'held'
        } else {
          centre = mid - step
          quietHoldRef.current = false
          _why += 'fit'
        }
        fitWinRef.current = { lo, hi, userH, centre, halfSpan }
      }

      // ── TICK FLOOR (ytickcap) ──
      // The user span never goes below one tick per target-height row. Under
      // log the view's price half is exp(c)*sinh(h), so the floor is
      // asinh(minHalfLin / exp(c)) — exact for the lattice's own mapping.
      // Re-checked after the centre clamp (a lower centre needs a wider ln
      // span for the same price half). Written back so the span IS the floor.
      const floorFor = (c) => {
        if (!(minHalfLin > 0) || !Number.isFinite(c)) return 0
        return log ? Math.asinh(minHalfLin / Math.exp(c)) : minHalfLin
      }
      let floor = floorFor(centre)
      if (userSpan && floor > 0 && halfSpan < floor) halfSpan = floor

      // ── CLAMP (user span only; every frame) ──
      // Raw visible extremes, not the hold. Data taller than the view: the
      // window stays inside the data. Data fits: all of it stays on screen
      // and a Y drag cannot move it. One row of slack either way (the fit
      // sits a row low, so arming from it must not jump).
      if (userSpan && Number.isFinite(min) && Number.isFinite(max) && Number.isFinite(centre)) {
        const slack = 2 * halfSpan + (step > 0 ? step : 0) // (yslack) full screen past data extremes
        // (yclamp) Pan limit = the whole LOADED data set (clampMin/Max), not
        // the visible scan, so X motion cannot drag a user span's centre.
        const cMin = Number.isFinite(clampMin) ? Math.min(clampMin, min) : min
        const cMax = Number.isFinite(clampMax) ? Math.max(clampMax, max) : max
        const clampC = (c, h) => {
          const a = cMin + h
          const b = cMax - h
          return a <= b
            ? Math.min(b + slack, Math.max(a - slack, c))
            : Math.min(a + slack, Math.max(b - slack, c))
        }
        centre = clampC(centre, halfSpan)
        const floor2 = floorFor(centre)
        if (floor2 > halfSpan) {
          halfSpan = floor2
          centre = clampC(centre, halfSpan)
        }
        if (floor2 > floor) floor = floor2
        userHalfRef.current = halfSpan
        if (mode === 'explore') userCentreRef.current = centre
        else if (mode === 'follow') followCentreRef.current = centre
      } else if (mode === 'follow') {
        followCentreRef.current = centre
      }

      floorRef.current = floor
      if (userSpan) quietHoldRef.current = false
      const holdQuiet = quietHoldRef.current
      lastRef.current = { centre, halfSpan }
      yfitT(`frame ${mode} ${_why}${stepChanged ? '+step' : ''}${holdQuiet ? '+quiet' : ''}${(xMoving && !tfHold && mode === 'fit') ? '+xstamp' : ''}`,
        { lo: min, hi: max, c: centre, h: halfSpan, extra: `hold=${Number.isFinite(lo) ? lo.toPrecision(6) : lo}..${Number.isFinite(hi) ? hi.toPrecision(6) : hi}` })

      return {
        mode,
        centre,
        fitCentre: centre,
        halfSpan,
        dataRange,
        scaleMoving: gesturing,
        holdQuiet,
      }
    },

    /**
     * Sub-bucket slide, in BUCKETS, never pixels. In [0,1) by construction.
     * step is the FINAL grid step (post coarsen/refine).
     */
    slide(desiredTop, step) {
      if (!(step > 0) || !Number.isFinite(desiredTop)) return 0
      const topSnap = Math.ceil(desiredTop / step) * step
      let rb = (topSnap - desiredTop) / step
      if (!(rb >= 0) || rb >= 1) rb = 0
      return rb
    },

    rowHeight: rowHeightForSpan,
  }), [markGesture, setDragActive, clearGesture])
}
